import { card, createDeck, resolveDeck } from "./cards.ts";
import { addModifier, canUseEffect, effectiveStat, fieldEffects, hasArchetype, hasEffect, markEffectUsed } from "./effects.ts";
import type { BallState, CardInstance, CommandResult, ConstructedDeck, FieldPlayer, GameCommand, GameState, PlayerSide, PublicGameState, Row, TeamState, Zone } from "./types.ts";

const other = (side: PlayerSide): PlayerSide => (side === "home" ? "away" : "home");
const forwardDelta = (side: PlayerSide) => (side === "home" ? -1 : 1);
const backRow = (side: PlayerSide): Row => (side === "home" ? 2 : 0);
const finalRow = (side: PlayerSide): Row => (side === "home" ? 0 : 2);
const sameZone = (a: Zone, b: Zone) => a.column === b.column && a.row === b.row;
const clone = <T>(value: T): T => structuredClone(value);

export function newGame(id: string, now: string): GameState {
  return { id, status: "waiting", createdAt: now, updatedAt: now, turn: 0, activeSide: "home", teams: {}, field: [], ball: { kind: "loose", zone: { column: 1, row: 1 } }, lastAction: "Waiting for players", effectLog: [], gameEffectUses: {} };
}

function shuffledDeck(side: PlayerSide, deckSelection: string | ConstructedDeck, random: () => number): CardInstance[] {
  const deck = createDeck(side, deckSelection);
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [deck[i], deck[j]] = [deck[j]!, deck[i]!];
  }
  return deck;
}

function drawToFive(team: TeamState) {
  while (team.hand.length < 5 && team.deck.length) team.hand.push(team.deck.shift()!);
}

function ensureOpeningPlayer(team: TeamState) {
  if (team.hand.some((held) => card(held.definitionId).role === "field" && card(held.definitionId).substitutions === 0)) return;
  const index = team.deck.findIndex((held) => card(held.definitionId).role === "field" && card(held.definitionId).substitutions === 0);
  if (index < 0) return;
  const [playerCard] = team.deck.splice(index, 1);
  const replaced = team.hand.pop();
  if (replaced) team.deck.push(replaced);
  team.hand.push(playerCard!);
}

export function addPlayer(state: GameState, side: PlayerSide, userId: string, name: string, now: string, random: () => number = Math.random, deckSelection: string | ConstructedDeck = "aurora"): GameState {
  if (state.teams[side]) throw new Error("That seat is already occupied.");
  const next = clone(state);
  const starter = resolveDeck(deckSelection);
  next.teams[side] = { userId, name: name.trim().slice(0, 24), deck: shuffledDeck(side, deckSelection, random), hand: [], bench: [], goalkeeper: { definitionId: starter.goalkeeperId, health: card(starter.goalkeeperId).health, tapped: false, modifiers: [], effectUses: {} }, turnsCompleted: 0, starterDeckId: starter.id };
  if (next.teams.home && next.teams.away) {
    next.status = "active";
    next.turn = 1;
    drawToFive(next.teams.home);
    drawToFive(next.teams.away);
    ensureOpeningPlayer(next.teams.home);
    ensureOpeningPlayer(next.teams.away);
    next.lastAction = `${next.teams.home.name} kicks off`;
  }
  next.updatedAt = now;
  return next;
}

function player(state: GameState, id: string): FieldPlayer {
  const result = state.field.find((item) => item.instanceId === id);
  if (!result) throw new Error("Player is no longer on the field.");
  return result;
}

function teamFor(state: GameState, side: PlayerSide): TeamState {
  const result = state.teams[side];
  if (!result) throw new Error("Team not found.");
  return result;
}

function assertCommandAllowed(state: GameState, side: PlayerSide, command: GameCommand) {
  if (state.status !== "active") throw new Error("The game is not active.");
  if (state.reaction) {
    if (state.reaction.offeredTo !== side) throw new Error("Waiting for the opponent to respond.");
    if (command.type !== "playReaction" && command.type !== "declineReaction") throw new Error("Play a reaction or pass priority.");
    return;
  }
  if (command.type === "playReaction" || command.type === "declineReaction") throw new Error("There is no reaction window.");
  if (state.activeSide !== side) throw new Error("It is not your turn.");
}

function assertReady(state: GameState, actor: FieldPlayer) {
  if (teamFor(state, actor.controller).turnsCompleted === 0) throw new Error("Players cannot move or act during their team's first turn.");
  if (actor.tapped) throw new Error("That player is tapped.");
}

function dealDamage(state: GameState, target: FieldPlayer, amount: number): number {
  const incoming = Math.max(0, amount);
  const resolved = hasEffect(target.definitionId, "immovable") ? Math.max(0, incoming - 2) : incoming;
  target.health -= resolved;
  if (incoming !== resolved) noteEffect(state, `${card(target.definitionId).name}: Immovable prevented ${incoming - resolved} damage`);
  if (resolved > 0 && target.health > 0 && hasEffect(target.definitionId, "duel_hardened")) {
    addModifier(target, target.instanceId, "duel_hardened", { steal: 2 }, state.turn);
    noteEffect(state, `${card(target.definitionId).name}: Duel-Hardened (+2 Steal)`);
  }
  return resolved;
}

function noteEffect(state: GameState, message: string) {
  state.effectLog = [...(state.effectLog ?? []), message].slice(-8);
}

function untapByEffect(state: GameState, target: FieldPlayer, sourceName: string): boolean {
  if (target.effectUntappedTurn === state.turn) return false;
  target.tapped = false;
  target.effectUntappedTurn = state.turn;
  noteEffect(state, `${sourceName} untapped ${card(target.definitionId).name}`);
  return true;
}

function resolvePassEffects(state: GameState, side: PlayerSide, actor: FieldPlayer, receiver: FieldPlayer) {
  for (const source of fieldEffects(state, side, "building_momentum")) {
    addModifier(source, source.instanceId, "building_momentum", { power: 1 }, state.turn);
    noteEffect(state, `${card(source.definitionId).name}: Building Momentum (+1 Power)`);
  }
  if (hasEffect(actor.definitionId, "clinical_pass")) {
    addModifier(receiver, actor.instanceId, "clinical_pass", { power: 3 }, state.turn, "shoot");
    noteEffect(state, `Clinical Pass gave ${card(receiver.definitionId).name} +3 Power for its next shot`);
  }
  if (hasEffect(actor.definitionId, "one_two") && canUseEffect(actor, "one_two", state.turn)) {
    if (untapByEffect(state, receiver, "One-Two")) {
      receiver.cannotPassTurn = state.turn;
      markEffectUsed(actor, "one_two", state.turn);
    }
  }
  if (hasEffect(actor.definitionId, "whipped_delivery")) {
    addModifier(receiver, actor.instanceId, "whipped_delivery", { power: 1, dribble: 1 }, state.turn);
    noteEffect(state, `${card(actor.definitionId).name}: Whipped Delivery gave ${card(receiver.definitionId).name} +1 Power and Dribble`);
  }
  if (hasEffect(receiver.definitionId, "soft_landing")) {
    addModifier(receiver, receiver.instanceId, "soft_landing", { dribble: 2 }, state.turn);
    noteEffect(state, `${card(receiver.definitionId).name}: Soft Landing (+2 Dribble)`);
  }
  if (hasEffect(receiver.definitionId, "first_time_finish")) {
    addModifier(receiver, receiver.instanceId, "first_time_finish", { power: 2 }, state.turn, "shoot");
    noteEffect(state, `${card(receiver.definitionId).name}: First-Time Finish (+2 Power for its next shot)`);
  }
  if (hasEffect(receiver.definitionId, "aerial_threat")) {
    receiver.health = Math.min(card(receiver.definitionId).health, receiver.health + 2);
    addModifier(receiver, receiver.instanceId, "aerial_threat", { power: 2 }, state.turn, "shoot");
    noteEffect(state, `${card(receiver.definitionId).name}: Aerial Threat healed 2 and prepared +2 Power`);
  }
  if (hasEffect(receiver.definitionId, "conduct_attack") && canUseEffect(receiver, "conduct_attack", state.turn)) {
    if (untapByEffect(state, actor, "Conduct the Attack")) {
      actor.cannotPassTurn = state.turn;
      markEffectUsed(receiver, "conduct_attack", state.turn);
    }
  }
  if (hasEffect(receiver.definitionId, "championship_spark") && receiver.zone.row === finalRow(side) && canUseEffect(receiver, "championship_spark", state.turn)) {
    if (untapByEffect(state, receiver, "Championship Spark")) markEffectUsed(receiver, "championship_spark", state.turn);
  }
  if (hasEffect(actor.definitionId, "no_look_release") && canUseEffect(actor, "no_look_release", state.turn)) {
    if (untapByEffect(state, actor, "No-Look Release")) {
      actor.cannotPassTurn = state.turn;
      markEffectUsed(actor, "no_look_release", state.turn);
    }
  }
  if (hasEffect(receiver.definitionId, "ghost_run") && canUseEffect(receiver, "ghost_run", state.turn)) {
    const row = receiver.zone.row + forwardDelta(side);
    if (row >= 0 && row <= 2) {
      receiver.zone.row = row as Row;
      receiver.moved = true;
      markEffectUsed(receiver, "ghost_run", state.turn);
      noteEffect(state, `${card(receiver.definitionId).name}: Ghost Run advanced one row`);
    }
  }
  const tikiPair = hasArchetype(actor.definitionId, "tiki_taka") && hasArchetype(receiver.definitionId, "tiki_taka");
  if (tikiPair) {
    if (hasEffect(actor.definitionId, "give_and_go")) addModifier(actor, actor.instanceId, "give_and_go", { dribble: 1 }, state.turn);
    if (hasEffect(receiver.definitionId, "silk_receiver")) addModifier(receiver, receiver.instanceId, "silk_receiver", { power: 1, dribble: 1 }, state.turn);
    for (const source of fieldEffects(state, side, "tempo_conductor").filter(source => source.instanceId !== actor.instanceId)) addModifier(source, source.instanceId, "tempo_conductor", { power: 1 }, state.turn);
    if (hasEffect(actor.definitionId, "la_pausa") && canUseEffect(actor, "la_pausa", state.turn) && untapByEffect(state, actor, "La Pausa")) {
      actor.cannotPassTurn = state.turn;
      markEffectUsed(actor, "la_pausa", state.turn);
    }
    if (hasEffect(receiver.definitionId, "split_the_line") && canUseEffect(receiver, "split_the_line", state.turn)) {
      const row = receiver.zone.row + forwardDelta(side);
      if (row >= 0 && row <= 2) {
        receiver.zone.row = row as Row;
        receiver.moved = true;
        markEffectUsed(receiver, "split_the_line", state.turn);
        noteEffect(state, `${card(receiver.definitionId).name}: Split the Line advanced one row`);
      }
    }
  }
  const counterPair = hasArchetype(actor.definitionId, "counterattack") && hasArchetype(receiver.definitionId, "counterattack");
  if (counterPair && hasEffect(receiver.definitionId, "quick_release")) addModifier(receiver, receiver.instanceId, "quick_release", { power: 2 }, state.turn, "shoot");
  const setPiecePair = hasArchetype(actor.definitionId, "set_piece") && hasArchetype(receiver.definitionId, "set_piece") && receiver.zone.row === finalRow(side);
  if (setPiecePair) {
    if (hasEffect(actor.definitionId, "dead_ball_delivery")) addModifier(receiver, actor.instanceId, "dead_ball_delivery", { power: 2 }, state.turn, "shoot");
    if (hasEffect(actor.definitionId, "whipped_specialist")) addModifier(receiver, actor.instanceId, "whipped_specialist", { power: 2, dribble: 1 }, state.turn);
    if (hasEffect(receiver.definitionId, "towering_target")) {
      receiver.health = Math.min(card(receiver.definitionId).health, receiver.health + 1);
      addModifier(receiver, receiver.instanceId, "towering_target", { power: 2 }, state.turn, "shoot");
    }
  }
}

function resolveMidfieldSovereign(state: GameState, actor: FieldPlayer) {
  if (!hasEffect(actor.definitionId, "midfield_sovereign") || !canUseEffect(actor, "midfield_sovereign", state.turn)) return;
  untapByEffect(state, actor, "Midfield Sovereign");
  for (const ally of state.field.filter(candidate => candidate.controller === actor.controller && candidate.instanceId !== actor.instanceId && candidate.zone.row === actor.zone.row)) {
    addModifier(ally, actor.instanceId, "midfield_sovereign", { power: 1, dribble: 1, steal: 1, defense: 1 }, state.turn);
  }
  markEffectUsed(actor, "midfield_sovereign", state.turn);
  noteEffect(state, `${card(actor.definitionId).name}: Midfield Sovereign strengthened its row`);
}

function resolveArchetypeSteal(state: GameState, actor: FieldPlayer, lostCarrier?: FieldPlayer) {
  if (hasEffect(actor.definitionId, "turnover_burst")) addModifier(actor, actor.instanceId, "turnover_burst", { power: 3 }, state.turn, "shoot");
  if (hasEffect(actor.definitionId, "transition_finish")) {
    addModifier(actor, actor.instanceId, "transition_finish", { dribble: 1 }, state.turn);
    addModifier(actor, actor.instanceId, "transition_finish_shot", { power: 3 }, state.turn, "shoot");
  }
  if (lostCarrier) {
    for (const source of fieldEffects(state, lostCarrier.controller, "five_second_rule").filter(source => source.zone.row === lostCarrier.zone.row && canUseEffect(source, "five_second_rule", state.turn))) {
      untapByEffect(state, source, "Five-Second Rule");
      addModifier(source, source.instanceId, "five_second_rule", { steal: 1 }, state.turn);
      markEffectUsed(source, "five_second_rule", state.turn);
    }
  }
  if (!hasArchetype(actor.definitionId, "counterattack")) return;
  for (const source of fieldEffects(state, actor.controller, "outlet_runner").filter(source => source.instanceId !== actor.instanceId && canUseEffect(source, "outlet_runner", state.turn))) {
    const row = source.zone.row + forwardDelta(source.controller);
    if (row >= 0 && row <= 2) {
      source.zone.row = row as Row;
      source.moved = true;
      markEffectUsed(source, "outlet_runner", state.turn);
    }
  }
  for (const source of fieldEffects(state, actor.controller, "recovery_sprint").filter(source => source.instanceId !== actor.instanceId && canUseEffect(source, "recovery_sprint", state.turn))) {
    if (untapByEffect(state, source, "Recovery Sprint")) markEffectUsed(source, "recovery_sprint", state.turn);
  }
  const legend = fieldEffects(state, actor.controller, "lightning_counter").find(source => canUseEffect(source, "lightning_counter", state.turn));
  if (legend) {
    untapByEffect(state, actor, "Lightning Counter");
    for (const ally of state.field.filter(source => source.controller === actor.controller && hasArchetype(source.definitionId, "counterattack"))) addModifier(ally, legend.instanceId, "lightning_counter", { power: 1, dribble: 1 }, state.turn);
    markEffectUsed(legend, "lightning_counter", state.turn);
  }
}

function resolveShotEffects(state: GameState, shooter: FieldPlayer, goalkeeperPossession?: PlayerSide) {
  if (state.status === "complete") return;
  for (const side of ["home", "away"] as const) {
    const team = state.teams[side];
    if (!team) continue;
    if (hasEffect(team.goalkeeper.definitionId, "rallying_saves")) {
      const maximum = card(team.goalkeeper.definitionId).health;
      const before = team.goalkeeper.health;
      team.goalkeeper.health = Math.min(maximum, before + 1);
      if (team.goalkeeper.health > before) noteEffect(state, `${card(team.goalkeeper.definitionId).name}: Rallying Saves healed 1 Health`);
    }
  }
  if (hasArchetype(shooter.definitionId, "set_piece")) {
    for (const source of fieldEffects(state, shooter.controller, "second_ball").filter(source => canUseEffect(source, "second_ball", state.turn))) {
      untapByEffect(state, source, "Second Ball");
      addModifier(source, source.instanceId, "second_ball", { steal: 1 }, state.turn);
      markEffectUsed(source, "second_ball", state.turn);
    }
  }
  if (goalkeeperPossession) {
    const goalkeeper = teamFor(state, goalkeeperPossession).goalkeeper;
    if (hasEffect(goalkeeper.definitionId, "counterattack_keeper")) {
      goalkeeper.pendingPassBuff = true;
      noteEffect(state, `${card(goalkeeper.definitionId).name} prepared a counterattacking pass`);
    }
  }
}

function drawOne(state: GameState, side: PlayerSide, sourceName: string): boolean {
  const team = teamFor(state, side);
  const drawn = team.deck.shift();
  if (!drawn) return false;
  team.hand.push(drawn);
  noteEffect(state, `${sourceName} added ${card(drawn.definitionId).name} to hand`);
  return true;
}

function resolveBenchedEffects(state: GameState, benched: CardInstance, side: PlayerSide) {
  const team = teamFor(state, side);
  if (hasEffect(benched.definitionId, "next_player_up")) drawOne(state, side, "Next Player Up");
  if (hasEffect(benched.definitionId, "second_wind")) {
    const useKey = `${benched.instanceId}:second_wind`;
    if (!state.gameEffectUses[useKey]) {
      const index = team.bench.findIndex(candidate => candidate.instanceId === benched.instanceId);
      if (index >= 0) team.hand.push(team.bench.splice(index, 1)[0]!);
      state.gameEffectUses[useKey] = true;
      noteEffect(state, `${card(benched.definitionId).name}: Second Wind returned it to hand`);
    }
  }
}

function benchCard(state: GameState, side: PlayerSide, benched: CardInstance) {
  teamFor(state, side).bench.push(benched);
  noteEffect(state, `${card(benched.definitionId).name} was benched`);
  resolveBenchedEffects(state, benched, side);
}

function sendToBench(state: GameState, target: FieldPlayer, newBall?: BallState) {
  const index = state.field.findIndex((item) => item.instanceId === target.instanceId);
  if (index >= 0) state.field.splice(index, 1);
  const benched = { instanceId: target.instanceId, definitionId: target.definitionId };
  if (newBall && state.ball.kind === "player" && state.ball.playerId === target.instanceId) state.ball = newBall;
  benchCard(state, target.controller, benched);
}

function removeDestroyed(state: GameState, target: FieldPlayer, newBall: BallState): boolean {
  if (target.health > 0) return false;
  sendToBench(state, target, newBall);
  return true;
}

function findHandCard(team: TeamState, id: string): CardInstance {
  const result = team.hand.find((item) => item.instanceId === id);
  if (!result) throw new Error("That card is not in your hand.");
  return result;
}

function spendHandCard(team: TeamState, id: string): CardInstance {
  const index = team.hand.findIndex((item) => item.instanceId === id);
  if (index < 0) throw new Error("That card is not in your hand.");
  return team.hand.splice(index, 1)[0]!;
}

function openColumn(state: GameState, side: PlayerSide, zone: Zone): boolean {
  return state.field.filter((item) => item.controller === side).length < 3
    && !state.field.some((item) => item.controller === side && item.zone.column === zone.column);
}

function canPlayPassReaction(state: GameState, side: PlayerSide, held: CardInstance): boolean {
  const reaction = state.reaction;
  if (!reaction || reaction.kind !== "pass" || reaction.offeredTo !== side) return false;
  const definition = card(held.definitionId);
  if (definition.effects.includes("read_the_lane") || definition.effects.includes("close_the_receiver")) return openColumn(state, side, reaction.destination);
  if (definition.effects.includes("mark_the_passer")) return openColumn(state, side, reaction.origin);
  if (definition.effects.includes("tactic_cut_lane")) return state.field.some((item) => item.controller === side && sameZone(item.zone, reaction.destination));
  if (definition.effects.includes("tactic_offside_trap")) return reaction.destination.row === backRow(side);
  return false;
}

function offerPassReaction(state: GameState, actionSide: PlayerSide, passer: FieldPlayer, receiver: FieldPlayer, origin: Zone) {
  const offeredTo = other(actionSide);
  const reaction = { kind: "pass" as const, actionSide, offeredTo, passerId: passer.instanceId, receiverId: receiver.instanceId, origin: clone(origin), destination: clone(receiver.zone) };
  state.reaction = reaction;
  const team = teamFor(state, offeredTo);
  if (!team.hand.some((held) => canPlayPassReaction(state, offeredTo, held))) delete state.reaction;
}

function resolveShot(state: GameState, side: PlayerSide, actor: FieldPlayer, powerPenalty = 0, forceIgnoreDefense = false): string {
  actor.tapped = true;
  const opposingSide = other(side);
  const goalkeeper = teamFor(state, opposingSide).goalkeeper;
  const defender = state.field.find((item) => item.controller === opposingSide && sameZone(item.zone, actor.zone));
  const hammerBonus = hasEffect(actor.definitionId, "hammer_finish")
    ? 1 + state.field.filter((item) => item.controller === opposingSide && item.zone.row === actor.zone.row).length
    : 0;
  const power = Math.max(0, effectiveStat(state, actor, "power") + hammerBonus - powerPenalty);
  if (hammerBonus) noteEffect(state, `${card(actor.definitionId).name}: Hammer Finish gained +${hammerBonus} Power from the opposing row`);
  actor.modifiers = actor.modifiers.filter((modifier) => modifier.consumeOn !== "shoot");
  let summary: string;
  if (defender) {
    const screened = hasArchetype(actor.definitionId, "set_piece") && state.field.some(item => item.controller === side && item.instanceId !== actor.instanceId && item.zone.row === actor.zone.row && hasEffect(item.definitionId, "screen_keeper"));
    const ignoresDefense = hasEffect(actor.definitionId, "hammer_finish") || forceIgnoreDefense || screened;
    const damage = dealDamage(state, defender, ignoresDefense ? power : Math.max(0, power - effectiveStat(state, defender, "defense")));
    if (ignoresDefense) noteEffect(state, `${card(actor.definitionId).name}'s shot ignored ${card(defender.definitionId).name}'s Defense`);
    const destroyed = removeDestroyed(state, defender, { kind: "goalkeeper", side: opposingSide });
    if (destroyed && goalkeeper.health <= 0) {
      state.status = "complete";
      state.winner = side;
      state.ball = { kind: "goal", side: opposingSide };
      summary = `${card(actor.definitionId).name} smashed through the final defender to win!`;
    } else {
      state.ball = { kind: "goalkeeper", side: opposingSide };
      summary = `The defender blocked the shot and took ${damage} damage`;
    }
  } else if (goalkeeper.health <= 0) {
    state.status = "complete";
    state.winner = side;
    state.ball = { kind: "goal", side: opposingSide };
    summary = `${card(actor.definitionId).name} scored the winning goal!`;
  } else {
    let damage = Math.max(0, power - card(goalkeeper.definitionId).defense);
    if (hasEffect(goalkeeper.definitionId, "miracle_save") && damage >= 4 && goalkeeper.effectUses.miracle_save !== "game") {
      damage = 1;
      goalkeeper.effectUses.miracle_save = "game";
      noteEffect(state, `${card(goalkeeper.definitionId).name}: Miracle Save reduced the shot to 1 damage`);
    }
    if (hasEffect(goalkeeper.definitionId, "safe_hands") && damage > 0) {
      damage -= 1;
      noteEffect(state, `${card(goalkeeper.definitionId).name}: Safe Hands prevented 1 damage`);
    }
    goalkeeper.health = Math.max(0, goalkeeper.health - damage);
    if (goalkeeper.health === 0) {
      state.status = "complete";
      state.winner = side;
      state.ball = { kind: "goal", side: opposingSide };
      summary = `${card(actor.definitionId).name} scored the winning goal!`;
    } else {
      state.ball = { kind: "goalkeeper", side: opposingSide };
      summary = `${card(actor.definitionId).name} hit the goalkeeper for ${damage}${powerPenalty ? ` (${powerPenalty} range penalty)` : ""}`;
    }
  }
  resolveShotEffects(state, actor, state.status === "complete" ? undefined : opposingSide);
  return summary;
}

export function executeCommand(source: GameState, side: PlayerSide, command: GameCommand, now: string, random: () => number = Math.random): CommandResult {
  assertCommandAllowed(source, side, command);
  const state = clone(source);
  const team = teamFor(state, side);
  let summary = "";
  let die: number | undefined;

  if (command.type === "declineReaction") {
    delete state.reaction;
    summary = `${team.name} passed priority`;
  } else if (command.type === "playReaction") {
    const held = findHandCard(team, command.cardId);
    if (!canPlayPassReaction(state, side, held)) throw new Error("That card cannot react to this pass.");
    const reaction = state.reaction!;
    const definition = card(held.definitionId);
    if (definition.role === "field") {
      const destination = definition.effects.includes("mark_the_passer") ? reaction.origin : reaction.destination;
      const used = spendHandCard(team, command.cardId);
      const summoned: FieldPlayer = { ...used, controller: side, zone: clone(destination), health: definition.health, tapped: false, moved: false, modifiers: [], effectUses: {} };
      state.field.push(summoned);
      if (definition.effects.includes("read_the_lane")) {
        state.ball = { kind: "player", playerId: summoned.instanceId };
        summary = `${definition.name} read the pass and took possession`;
      } else if (definition.effects.includes("close_the_receiver")) {
        const receiver = player(state, reaction.receiverId);
        receiver.tapped = true;
        summary = `${definition.name} closed down ${card(receiver.definitionId).name}`;
      } else {
        const passer = player(state, reaction.passerId);
        const damage = dealDamage(state, passer, 2);
        removeDestroyed(state, passer, state.ball);
        summary = `${definition.name} marked the passer for ${damage} damage`;
      }
    } else if (definition.effects.includes("tactic_cut_lane")) {
      const interceptor = command.targetPlayerId
        ? player(state, command.targetPlayerId)
        : state.field.find((item) => item.controller === side && sameZone(item.zone, reaction.destination));
      if (!interceptor || interceptor.controller !== side || !sameZone(interceptor.zone, reaction.destination)) throw new Error("Choose your player in the pass destination.");
      const used = spendHandCard(team, command.cardId);
      benchCard(state, side, used);
      state.ball = { kind: "player", playerId: interceptor.instanceId };
      summary = `${card(interceptor.definitionId).name} cut the lane and took possession`;
    } else {
      const receiver = player(state, reaction.receiverId);
      const used = spendHandCard(team, command.cardId);
      benchCard(state, side, used);
      receiver.zone.row = 1;
      summary = `${team.name} sprung the offside trap on ${card(receiver.definitionId).name}`;
    }
    noteEffect(state, summary);
    delete state.reaction;
  } else if (command.type === "mulligan") {
    if (team.mulliganTurn === state.turn) throw new Error("You have already used your mulligan this turn.");
    const ids = [...new Set(command.cardIds)];
    if (!ids.length) throw new Error("Choose at least one card to mulligan.");
    if (ids.length !== command.cardIds.length) throw new Error("Choose each mulligan card only once.");
    if (team.deck.length < ids.length) throw new Error("Your deck does not have enough replacement cards.");
    const returned = ids.map(id => findHandCard(team, id));
    const returnedIds = new Set(ids);
    team.hand = team.hand.filter(held => !returnedIds.has(held.instanceId));
    team.hand.push(...team.deck.splice(0, ids.length));
    team.deck.push(...returned);
    team.mulliganTurn = state.turn;
    summary = `${team.name} exchanged ${ids.length} card${ids.length === 1 ? "" : "s"} for a fresh draw`;
  } else if (command.type === "summon") {
    const held = findHandCard(team, command.cardId);
    const definition = card(held.definitionId);
    if (definition.role !== "field") throw new Error("Only player cards can be summoned.");
    if (definition.substitutions !== 0) throw new Error("This player must be substituted in.");
    if (state.field.filter((item) => item.controller === side).length >= 3) throw new Error("You already control three players.");
    if (state.field.some((item) => item.controller === side && item.zone.column === command.column)) throw new Error("You already control this column.");
    const used = spendHandCard(team, command.cardId);
    const summoned: FieldPlayer = { ...used, controller: side, zone: { column: command.column, row: backRow(side) }, health: definition.health, tapped: false, moved: false, modifiers: [], effectUses: {} };
    state.field.push(summoned);
    if (hasEffect(summoned.definitionId, "early_read") && team.deck.length) {
      team.hand.push(team.deck.shift()!);
      noteEffect(state, `${definition.name}: Early Read drew a card`);
    }
    summary = `${team.name} summoned ${definition.name}`;
  } else if (command.type === "substitute") {
    const held = findHandCard(team, command.cardId);
    const definition = card(held.definitionId);
    if (!definition.substitutions) throw new Error("This player can be summoned normally.");
    const ids = [...new Set(command.removePlayerIds)];
    if (ids.length !== definition.substitutions) throw new Error(`Choose exactly ${definition.substitutions} player(s) to substitute.`);
    const removed = ids.map((id) => player(state, id));
    if (removed.some((item) => item.controller !== side)) throw new Error("You may only substitute your own players.");
    if (removed.some((item) => state.ball.kind === "player" && state.ball.playerId === item.instanceId)) throw new Error("The ball carrier cannot be substituted.");
    const zone = clone(removed[0]!.zone);
    for (const item of removed) {
      sendToBench(state, item);
    }
    const used = spendHandCard(team, command.cardId);
    const summoned: FieldPlayer = { ...used, controller: side, zone, health: definition.health, tapped: false, moved: false, modifiers: [], effectUses: {} };
    state.field.push(summoned);
    if (hasEffect(summoned.definitionId, "impact_substitute")) {
      addModifier(summoned, summoned.instanceId, "impact_substitute", { power: 2, dribble: 2, steal: 2 }, state.turn);
      noteEffect(state, `${definition.name}: Impact Substitute (+2 Power, Dribble, and Steal)`);
    }
    if (hasEffect(summoned.definitionId, "fresh_legs") && command.effectTargetId) {
      const target = player(state, command.effectTargetId);
      if (target.controller !== side || target.instanceId === summoned.instanceId) throw new Error("Fresh Legs must target another allied player.");
      untapByEffect(state, target, "Fresh Legs");
    }
    summary = `${team.name} substituted in ${definition.name}`;
  } else if (command.type === "move") {
    const actor = player(state, command.playerId);
    if (actor.controller !== side) throw new Error("That is not your player.");
    if (team.turnsCompleted === 0) throw new Error("Players cannot move or act during their team's first turn.");
    if (actor.moved) throw new Error("That player has already moved.");
    const delta = command.direction === "forward" ? forwardDelta(side) : -forwardDelta(side);
    if (command.direction === "forward" && state.field.some((item) => item.controller !== side && sameZone(item.zone, actor.zone))) throw new Error("An opponent blocks forward movement.");
    const row = actor.zone.row + delta;
    if (row < 0 || row > 2) throw new Error("That move leaves the field.");
    actor.zone.row = row as Row;
    actor.moved = true;
    if (state.ball.kind === "loose" && sameZone(state.ball.zone, actor.zone)) state.ball = { kind: "player", playerId: actor.instanceId };
    if (command.direction === "forward" && hasEffect(actor.definitionId, "breakaway")) {
      addModifier(actor, actor.instanceId, "breakaway", { power: 2 }, state.turn);
      noteEffect(state, `${card(actor.definitionId).name}: Breakaway (+2 Power)`);
    }
    if (command.direction === "forward" && hasEffect(actor.definitionId, "vertical_threat")) {
      addModifier(actor, actor.instanceId, "vertical_threat", { power: 1, dribble: 1 }, state.turn);
      noteEffect(state, `${card(actor.definitionId).name}: Vertical Threat (+1 Power and Dribble)`);
    }
    if (command.direction === "backward" && hasEffect(actor.definitionId, "engine_room") && canUseEffect(actor, "engine_room", state.turn)) {
      if (untapByEffect(state, actor, "Engine Room")) markEffectUsed(actor, "engine_room", state.turn);
    }
    if (command.direction === "forward" && hasEffect(actor.definitionId, "afterburner") && canUseEffect(actor, "afterburner", state.turn)) {
      actor.moved = false;
      markEffectUsed(actor, "afterburner", state.turn);
      noteEffect(state, `${card(actor.definitionId).name}: Afterburner preserved its movement`);
    }
    summary = `${card(actor.definitionId).name} moved ${command.direction}`;
  } else if (command.type === "pass") {
    const actor = player(state, command.playerId);
    const target = player(state, command.targetPlayerId);
    if (actor.controller !== side || target.controller !== side) throw new Error("Passes must be between allies.");
    assertReady(state, actor);
    if (actor.cannotPassTurn === state.turn) throw new Error("One-Two prevents that player from passing again this turn.");
    if (state.ball.kind !== "player" || state.ball.playerId !== actor.instanceId) throw new Error("That player does not have the ball.");
    if (actor.zone.row !== target.zone.row) throw new Error("Field players pass only within the same row.");
    const origin = clone(actor.zone);
    actor.tapped = true;
    state.ball = { kind: "player", playerId: target.instanceId };
    resolvePassEffects(state, side, actor, target);
    summary = `${card(actor.definitionId).name} passed to ${card(target.definitionId).name}`;
    offerPassReaction(state, side, actor, target, origin);
  } else if (command.type === "keeperPass") {
    if (state.ball.kind !== "goalkeeper" || state.ball.side !== side) throw new Error("Your goalkeeper does not have the ball.");
    if (team.turnsCompleted === 0) throw new Error("Players cannot move or act during their team's first turn.");
    const target = player(state, command.targetPlayerId);
    if (target.controller !== side || (target.zone.row !== backRow(side) && target.zone.row !== 1)) throw new Error("The goalkeeper can pass only to your back or middle row.");
    if (team.goalkeeper.tapped) throw new Error("Your goalkeeper is tapped.");
    team.goalkeeper.tapped = true;
    state.ball = { kind: "player", playerId: target.instanceId };
    if (team.goalkeeper.pendingPassBuff) {
      addModifier(target, `goalkeeper:${side}`, "counterattack_keeper", { power: 2, dribble: 2 }, state.turn);
      team.goalkeeper.pendingPassBuff = false;
      noteEffect(state, `Counterattack Keeper gave ${card(target.definitionId).name} +2 Power and Dribble`);
    }
    if (hasEffect(team.goalkeeper.definitionId, "sweeper_release") && target.zone.row === 1) {
      addModifier(target, `goalkeeper:${side}`, "sweeper_release", { dribble: 2, steal: 2 }, state.turn);
      noteEffect(state, `${card(team.goalkeeper.definitionId).name}: Sweeper Release gave ${card(target.definitionId).name} +2 Dribble and Steal`);
    }
    summary = `${card(team.goalkeeper.definitionId).name} distributed the ball`;
  } else if (command.type === "dribble") {
    const actor = player(state, command.playerId);
    const target = player(state, command.targetPlayerId);
    if (actor.controller !== side || target.controller === side || !sameZone(actor.zone, target.zone)) throw new Error("Choose an opponent in the carrier's zone.");
    assertReady(state, actor);
    if (state.ball.kind !== "player" || state.ball.playerId !== actor.instanceId) throw new Error("That player does not have the ball.");
    actor.tapped = true;
    const ignoresDefense = hasEffect(actor.definitionId, "vanishing_step");
    const damage = dealDamage(state, target, ignoresDefense ? effectiveStat(state, actor, "dribble") : Math.max(0, effectiveStat(state, actor, "dribble") - effectiveStat(state, target, "defense")));
    if (ignoresDefense) noteEffect(state, `${card(actor.definitionId).name}: Vanishing Step ignored ${card(target.definitionId).name}'s Defense`);
    const destroyed = removeDestroyed(state, target, { kind: "player", playerId: actor.instanceId });
    if (destroyed) {
      const row = actor.zone.row + forwardDelta(side);
      if (row >= 0 && row <= 2) actor.zone.row = row as Row;
      if (hasEffect(actor.definitionId, "golden_left") && canUseEffect(actor, "golden_left", state.turn) && untapByEffect(state, actor, "Golden Left")) {
        actor.health = Math.min(card(actor.definitionId).health, actor.health + 2);
        addModifier(actor, actor.instanceId, "golden_left", { power: 2 }, state.turn, "shoot");
        markEffectUsed(actor, "golden_left", state.turn);
        noteEffect(state, `${card(actor.definitionId).name}: Golden Left healed 2 and prepared +2 Power for its next shot`);
      }
    }
    if (hasEffect(actor.definitionId, "close_control") && canUseEffect(actor, "close_control", state.turn) && untapByEffect(state, actor, "Close Control")) {
      markEffectUsed(actor, "close_control", state.turn);
    }
    summary = `${card(actor.definitionId).name} dribbled for ${damage} damage${destroyed ? " and broke through" : ""}`;
  } else if (command.type === "steal") {
    const actor = player(state, command.playerId);
    const target = player(state, command.targetPlayerId);
    if (actor.controller !== side || target.controller === side || !sameZone(actor.zone, target.zone)) throw new Error("Choose the opposing carrier in this zone.");
    assertReady(state, actor);
    if (state.ball.kind !== "player" || state.ball.playerId !== target.instanceId) throw new Error("That opponent does not have the ball.");
    actor.tapped = true;
    const stats = card(actor.definitionId);
    const cleanTackle = hasEffect(actor.definitionId, "clean_tackle");
    const steal = effectiveStat(state, actor, "steal");
    const damage = dealDamage(state, target, cleanTackle ? 0 : Math.max(0, steal - effectiveStat(state, target, "defense")));
    const destroyed = removeDestroyed(state, target, { kind: "player", playerId: actor.instanceId });
    die = Math.floor(random() * 6) + 1;
    const rollBonus = cleanTackle ? 3 : 0;
    const success = destroyed || die + steal + rollBonus >= effectiveStat(state, target, "dribble") + 4;
    if (success) {
      state.ball = { kind: "player", playerId: actor.instanceId };
      if (hasEffect(actor.definitionId, "ball_winner")) {
        const before = actor.health;
        actor.health = Math.min(card(actor.definitionId).health, actor.health + 2);
        if (actor.health > before) noteEffect(state, `${card(actor.definitionId).name}: Ball Winner healed ${actor.health - before}`);
      }
      resolveMidfieldSovereign(state, actor);
      resolveArchetypeSteal(state, actor, target);
    } else if (hasEffect(actor.definitionId, "second_challenge") && canUseEffect(actor, "second_challenge", state.turn) && untapByEffect(state, actor, "Second Challenge")) {
      markEffectUsed(actor, "second_challenge", state.turn);
    }
    summary = `${stats.name} dealt ${damage} and ${success ? "won" : "failed to win"} the ball (rolled ${die}${rollBonus ? ` + ${rollBonus} Clean Tackle` : ""})`;
  } else if (command.type === "keeperSteal") {
    const actor = player(state, command.playerId);
    const opposingSide = other(side);
    if (actor.controller !== side || actor.zone.row !== finalRow(side)) throw new Error("Only a player in the opponent's final row can press the goalkeeper.");
    if (state.ball.kind !== "goalkeeper" || state.ball.side !== opposingSide) throw new Error("The opposing goalkeeper does not have the ball.");
    assertReady(state, actor);
    actor.tapped = true;
    const stats = card(actor.definitionId);
    const goalkeeper = teamFor(state, opposingSide).goalkeeper;
    const keeperStats = card(goalkeeper.definitionId);
    const cleanTackle = hasEffect(actor.definitionId, "clean_tackle");
    const steal = effectiveStat(state, actor, "steal");
    const damage = cleanTackle ? 0 : Math.max(0, steal - keeperStats.defense);
    goalkeeper.health = Math.max(0, goalkeeper.health - damage);
    die = Math.floor(random() * 6) + 1;
    const rollBonus = cleanTackle ? 3 : 0;
    const success = die + steal + rollBonus >= keeperStats.dribble + 4;
    if (success) {
      state.ball = { kind: "player", playerId: actor.instanceId };
      if (hasEffect(actor.definitionId, "ball_winner")) {
        const before = actor.health;
        actor.health = Math.min(card(actor.definitionId).health, actor.health + 2);
        if (actor.health > before) noteEffect(state, `${card(actor.definitionId).name}: Ball Winner healed ${actor.health - before}`);
      }
      resolveMidfieldSovereign(state, actor);
      resolveArchetypeSteal(state, actor);
    } else if (hasEffect(actor.definitionId, "second_challenge") && canUseEffect(actor, "second_challenge", state.turn) && untapByEffect(state, actor, "Second Challenge")) {
      markEffectUsed(actor, "second_challenge", state.turn);
    }
    summary = `${stats.name} pressed the goalkeeper for ${damage} and ${success ? "won" : "failed to win"} the ball (rolled ${die}${rollBonus ? ` + ${rollBonus} Clean Tackle` : ""})`;
  } else if (command.type === "attack") {
    const actor = player(state, command.playerId);
    const target = player(state, command.targetPlayerId);
    if (actor.controller !== side || target.controller === side || !sameZone(actor.zone, target.zone)) throw new Error("Choose an opponent in this zone.");
    assertReady(state, actor);
    actor.tapped = true;
    const bonus = hasEffect(actor.definitionId, "body_check") ? 2 : 0;
    const damage = dealDamage(state, target, Math.max(0, effectiveStat(state, actor, "steal") - effectiveStat(state, target, "defense")) + bonus);
    if (bonus) noteEffect(state, `${card(actor.definitionId).name}: Body Check added 2 damage`);
    const destroyed = removeDestroyed(state, target, { kind: "player", playerId: actor.instanceId });
    if (destroyed && command.advance) {
      const row = actor.zone.row + forwardDelta(side);
      if (row >= 0 && row <= 2) actor.zone.row = row as Row;
    }
    summary = `${card(actor.definitionId).name} attacked for ${damage}${destroyed && command.advance ? " and advanced" : ""}`;
  } else if (command.type === "activateEffect") {
    const actor = player(state, command.playerId);
    if (actor.controller !== side) throw new Error("That is not your player.");
    if (!hasEffect(actor.definitionId, command.effectId)) throw new Error("That player does not have this effect.");
    assertReady(state, actor);
    if (!canUseEffect(actor, command.effectId, state.turn)) throw new Error("That effect has already been used this turn.");
    if (command.effectId === "thread_the_needle") {
      const target = player(state, command.targetPlayerId ?? "");
      if (target.controller !== side || target.instanceId === actor.instanceId || target.zone.row !== actor.zone.row) throw new Error("Choose another allied player in this row.");
      addModifier(target, actor.instanceId, command.effectId, { power: 2 }, state.turn);
      noteEffect(state, `${card(actor.definitionId).name}: Thread the Needle gave ${card(target.definitionId).name} +2 Power`);
      summary = `${card(actor.definitionId).name} threaded the needle`;
    } else if (["locker_room_recall", "tactical_recycle", "academy_call_up"].includes(command.effectId)) {
      const benchIndex = team.bench.findIndex(candidate => candidate.instanceId === command.targetCardId);
      if (benchIndex < 0) throw new Error("Choose a player on your bench.");
      const target = team.bench[benchIndex]!;
      const targetName = card(target.definitionId).name;
      if (command.effectId === "locker_room_recall") {
        team.hand.push(team.bench.splice(benchIndex, 1)[0]!);
        noteEffect(state, `Locker Room Recall added ${targetName} to hand`);
        summary = `${card(actor.definitionId).name} recalled ${targetName}`;
      } else if (command.effectId === "tactical_recycle") {
        team.deck.push(team.bench.splice(benchIndex, 1)[0]!);
        drawOne(state, side, "Tactical Recycle");
        summary = `${card(actor.definitionId).name} recycled ${targetName}`;
      } else {
        const column = command.column;
        if (column === undefined || ![0, 1, 2].includes(column)) throw new Error("Choose an open back-row column.");
        if (card(target.definitionId).role !== "field" || card(target.definitionId).substitutions !== 0) throw new Error("Academy Call-Up can only summon a 0-substitution player.");
        if (state.field.filter(candidate => candidate.controller === side).length >= 3) throw new Error("You already control three players.");
        if (state.field.some(candidate => candidate.controller === side && candidate.zone.column === column)) throw new Error("You already control this column.");
        team.bench.splice(benchIndex, 1);
        state.field.push({ ...target, controller: side, zone: { column, row: backRow(side) }, health: card(target.definitionId).health, tapped: false, moved: false, modifiers: [], effectUses: {} });
        noteEffect(state, `Academy Call-Up summoned ${targetName} from the bench`);
        summary = `${card(actor.definitionId).name} called up ${targetName}`;
      }
    } else {
      throw new Error("This effect is not activated manually.");
    }
    actor.tapped = true;
    markEffectUsed(actor, command.effectId, state.turn);
  } else if (command.type === "playTactic") {
    if (team.turnsCompleted === 0) throw new Error("Players cannot move or act during their team's first turn.");
    const held = findHandCard(team, command.cardId);
    const definition = card(held.definitionId);
    if (definition.role !== "tactic") throw new Error("That is not a tactic card.");
    if (definition.effects.includes("tactic_cut_lane") || definition.effects.includes("tactic_offside_trap")) throw new Error("That tactic can only be played as a reaction.");
    const mine = state.field.filter((item) => item.controller === side);
    const carrierId = state.ball.kind === "player" ? state.ball.playerId : undefined;
    const carrier = carrierId ? state.field.find((item) => item.instanceId === carrierId) : undefined;
    const effectId = definition.effects[0]!;
    if (["tactic_rondo_drill", "tactic_third_man_run", "tactic_early_outlet", "tactic_corner_routine"].includes(effectId)) {
      if (!carrier || carrier.controller !== side) throw new Error("You need a field player with possession.");
      assertReady(state, carrier);
      const target = player(state, command.targetPlayerId ?? "");
      if (target.controller !== side || target.instanceId === carrier.instanceId) throw new Error("Choose another allied player.");
      const required = effectId === "tactic_early_outlet" ? "counterattack" : effectId === "tactic_corner_routine" ? "set_piece" : "tiki_taka";
      if (!hasArchetype(target.definitionId, required) || (effectId !== "tactic_corner_routine" && !hasArchetype(carrier.definitionId, required))) throw new Error(`This tactic requires ${definition.archetype} players.`);
      if (effectId === "tactic_rondo_drill" && target.zone.row !== carrier.zone.row) throw new Error("Rondo Drill requires two Aurora FC players in the same row.");
      if (effectId === "tactic_corner_routine" && target.zone.row !== finalRow(side)) throw new Error("Corner Routine requires a Crownport FC receiver in the opposing final row.");
      const origin = clone(carrier.zone);
      carrier.tapped = true;
      state.ball = { kind: "player", playerId: target.instanceId };
      resolvePassEffects(state, side, carrier, target);
      if (effectId === "tactic_rondo_drill") {
        untapByEffect(state, target, definition.name);
        target.cannotPassTurn = state.turn;
        addModifier(target, held.instanceId, effectId, { dribble: 1 }, state.turn);
      } else if (effectId === "tactic_third_man_run") {
        const row = target.zone.row + forwardDelta(side);
        if (row >= 0 && row <= 2) target.zone.row = row as Row;
      } else if (effectId === "tactic_early_outlet") {
        addModifier(target, held.instanceId, effectId, { power: 2 }, state.turn, "shoot");
      } else {
        addModifier(target, held.instanceId, effectId, { power: 3 }, state.turn, "shoot");
      }
      summary = `${team.name} played ${definition.name} to ${card(target.definitionId).name}`;
      offerPassReaction(state, side, carrier, target, origin);
    } else if (effectId === "tactic_total_control" || effectId === "tactic_no_escape") {
      const archetype = effectId === "tactic_total_control" ? "tiki_taka" : "gegenpress";
      const stats = effectId === "tactic_total_control" ? { power: 2, dribble: 2, steal: 2 } : { steal: 2, defense: 1 };
      for (const target of mine.filter(item => hasArchetype(item.definitionId, archetype))) addModifier(target, held.instanceId, effectId, stats, state.turn);
      if (effectId === "tactic_no_escape") {
        if (!carrier || carrier.controller === side) throw new Error("The opponent needs a field player with possession.");
        addModifier(carrier, held.instanceId, effectId, { dribble: -2 }, state.turn, undefined, side);
      }
      summary = `${team.name} played ${definition.name}`;
    } else if (effectId === "tactic_pressing_trap" || effectId === "tactic_swarm_ball" || effectId === "tactic_break_at_pace") {
      const target = player(state, command.targetPlayerId ?? "");
      const required = effectId === "tactic_break_at_pace" ? "counterattack" : "gegenpress";
      if (target.controller !== side || !hasArchetype(target.definitionId, required)) throw new Error("Choose a player from this tactic's archetype.");
      if (effectId === "tactic_pressing_trap") addModifier(target, held.instanceId, effectId, { steal: 3 }, state.turn);
      if (effectId === "tactic_swarm_ball") {
        if (!carrier || carrier.controller === side || carrier.zone.column !== target.zone.column) throw new Error("Choose an Ironvale AC player in the opposing carrier's column.");
        target.zone.row = carrier.zone.row;
        untapByEffect(state, target, definition.name);
      }
      if (effectId === "tactic_break_at_pace") {
        const row = target.zone.row + forwardDelta(side);
        if (row < 0 || row > 2) throw new Error("That player is already in the final row.");
        target.zone.row = row as Row;
        target.moved = true;
        addModifier(target, held.instanceId, effectId, { dribble: 2 }, state.turn);
      }
      summary = `${team.name} played ${definition.name} on ${card(target.definitionId).name}`;
    } else if (["tactic_end_to_end", "tactic_free_kick", "tactic_last_minute_winner"].includes(effectId)) {
      if (!carrier || carrier.controller !== side) throw new Error("You need a field player with possession.");
      assertReady(state, carrier);
      if (effectId === "tactic_end_to_end") {
        if (!hasArchetype(carrier.definitionId, "counterattack")) throw new Error("End to End requires an Eclipse United carrier.");
        const distance = Math.abs(carrier.zone.row - finalRow(side));
        const ahead = mine.filter(item => item.instanceId !== carrier.instanceId && hasArchetype(item.definitionId, "counterattack") && Math.abs(item.zone.row - finalRow(side)) < distance).length;
        summary = `${team.name} played ${definition.name}. ${resolveShot(state, side, carrier, Math.max(0, distance - ahead))}`;
      } else if (effectId === "tactic_free_kick") {
        if (!hasArchetype(carrier.definitionId, "set_piece") || carrier.zone.row === backRow(side)) throw new Error("Free Kick requires a Crownport FC carrier in the middle or final row.");
        summary = `${team.name} played ${definition.name}. ${resolveShot(state, side, carrier, Math.abs(carrier.zone.row - finalRow(side)), true)}`;
      } else {
        if (!hasArchetype(carrier.definitionId, "set_piece") || carrier.zone.row !== finalRow(side)) throw new Error("Last-Minute Winner requires a final-row Crownport FC carrier.");
        const support = mine.filter(item => item.instanceId !== carrier.instanceId && item.zone.row === finalRow(side) && hasArchetype(item.definitionId, "set_piece")).length;
        addModifier(carrier, held.instanceId, effectId, { power: support * 2 }, state.turn, "shoot");
        summary = `${team.name} played ${definition.name}. ${resolveShot(state, side, carrier, 0, true)}`;
      }
    } else if (definition.effects.includes("tactic_long_pass") || definition.effects.includes("tactic_switch_play")) {
      if (!carrier || carrier.controller !== side) throw new Error("You need a field player with possession.");
      assertReady(state, carrier);
      const target = player(state, command.targetPlayerId ?? "");
      if (target.controller !== side || target.instanceId === carrier.instanceId) throw new Error("Choose another allied player.");
      if (definition.effects.includes("tactic_switch_play") && target.zone.row !== carrier.zone.row) throw new Error("Switch the Play requires a receiver in the same row.");
      const origin = clone(carrier.zone);
      carrier.tapped = true;
      state.ball = { kind: "player", playerId: target.instanceId };
      resolvePassEffects(state, side, carrier, target);
      if (definition.effects.includes("tactic_switch_play")) addModifier(target, held.instanceId, "tactic_switch_play", { dribble: 2 }, state.turn);
      summary = `${team.name} played ${definition.name} to ${card(target.definitionId).name}`;
      offerPassReaction(state, side, carrier, target, origin);
    } else if (definition.effects.includes("tactic_instant_shot")) {
      if (!carrier || carrier.controller !== side) throw new Error("You need a field player with possession.");
      assertReady(state, carrier);
      const penalty = Math.abs(carrier.zone.row - finalRow(side));
      summary = `${team.name} played ${definition.name}. ${resolveShot(state, side, carrier, penalty)}`;
    } else if (definition.effects.includes("tactic_through_ball")) {
      if (!carrier || carrier.controller !== side) throw new Error("You need a field player with possession.");
      assertReady(state, carrier);
      if (state.field.some((item) => item.controller !== side && sameZone(item.zone, carrier.zone))) throw new Error("An opponent blocks the run.");
      const row = carrier.zone.row + forwardDelta(side);
      if (row < 0 || row > 2) throw new Error("The carrier is already in the final row.");
      carrier.zone.row = row as Row;
      carrier.moved = true;
      summary = `${team.name} sent ${card(carrier.definitionId).name} through`;
    } else if (definition.effects.includes("tactic_high_press") || definition.effects.includes("tactic_low_block")) {
      const stats = definition.effects.includes("tactic_high_press") ? { steal: 2 } : { defense: 1 };
      for (const target of mine) addModifier(target, held.instanceId, definition.effects[0]!, stats, state.turn);
      summary = `${team.name} set up a ${definition.name}`;
    } else {
      const target = player(state, command.targetPlayerId ?? command.playerId ?? "");
      if (target.controller !== side) throw new Error("Choose one of your players.");
      if (definition.effects.includes("tactic_power_strike")) {
        addModifier(target, held.instanceId, "tactic_power_strike", { power: 3 }, state.turn, "shoot");
      } else if (definition.effects.includes("tactic_treatment")) {
        if (target.health >= card(target.definitionId).health) throw new Error("Choose a damaged player.");
        target.health = Math.min(card(target.definitionId).health, target.health + 3);
      } else if (definition.effects.includes("tactic_quick_restart")) {
        if (!target.tapped) throw new Error("Choose a tapped player.");
        if (!untapByEffect(state, target, definition.name)) throw new Error("That player has already been untapped by an effect this turn.");
        target.cannotPassTurn = state.turn;
      } else if (definition.effects.includes("tactic_recovery_run")) {
        const row = target.zone.row - forwardDelta(side);
        if (row < 0 || row > 2) throw new Error("That player is already in your back row.");
        target.zone.row = row as Row;
        target.health = Math.min(card(target.definitionId).health, target.health + 2);
      } else {
        throw new Error("That tactic is not implemented.");
      }
      summary = `${team.name} played ${definition.name} on ${card(target.definitionId).name}`;
    }
    const used = spendHandCard(team, command.cardId);
    benchCard(state, side, used);
    noteEffect(state, summary);
  } else if (command.type === "shoot") {
    const actor = player(state, command.playerId);
    if (actor.controller !== side || actor.zone.row !== finalRow(side)) throw new Error("Only a carrier in the opponent's final row may shoot.");
    assertReady(state, actor);
    if (state.ball.kind !== "player" || state.ball.playerId !== actor.instanceId) throw new Error("That player does not have the ball.");
    summary = resolveShot(state, side, actor);
  } else if (command.type === "endTurn") {
    for (const item of state.field) item.modifiers = (item.modifiers ?? []).filter((modifier) => modifier.expiresAfterTurn === undefined || modifier.expiresAfterTurn > state.turn);
    team.turnsCompleted += 1;
    state.activeSide = other(side);
    state.turn += 1;
    for (const item of state.field) item.modifiers = (item.modifiers ?? []).filter((modifier) => modifier.expiresAtStartOfSide !== state.activeSide);
    const activeTeam = teamFor(state, state.activeSide);
    activeTeam.goalkeeper.tapped = false;
    for (const item of state.field.filter((candidate) => candidate.controller === state.activeSide)) {
      item.tapped = false;
      item.moved = false;
    }
    drawToFive(activeTeam);
    const commander = fieldEffects(state, state.activeSide, "royal_command")[0];
    if (commander) {
      for (const target of state.field.filter((candidate) => candidate.controller === state.activeSide)) {
        addModifier(target, commander.instanceId, "royal_command", { power: 1, dribble: 1, steal: 1 }, state.turn);
      }
      noteEffect(state, `${card(commander.definitionId).name}: Royal Command strengthened the team`);
    }
    summary = `${activeTeam.name}'s turn`;
  }

  state.updatedAt = now;
  state.lastAction = summary;
  state.lastActionType = command.type;
  delete state.lastActorId;
  delete state.lastTargetId;
  if ("playerId" in command) state.lastActorId = command.playerId;
  else if (command.type === "summon" || command.type === "substitute") state.lastActorId = command.cardId;
  else if (command.type === "keeperPass") state.lastActorId = `goalkeeper:${side}`;
  if ("targetPlayerId" in command && command.targetPlayerId) state.lastTargetId = command.targetPlayerId;
  else if (command.type === "shoot") state.lastTargetId = `goalkeeper:${other(side)}`;
  else if (command.type === "keeperSteal") state.lastTargetId = `goalkeeper:${other(side)}`;
  return { state, summary, ...(die === undefined ? {} : { die }) };
}

export function sideForUser(state: GameState, userId: string): PlayerSide | undefined {
  if (state.teams.home?.userId === userId) return "home";
  if (state.teams.away?.userId === userId) return "away";
  return undefined;
}

export function publicState(state: GameState, viewerSide?: PlayerSide): PublicGameState {
  const teams: PublicGameState["teams"] = {};
  for (const side of ["home", "away"] as const) {
    const source = state.teams[side];
    if (!source) continue;
    const { deck, hand, ...rest } = clone(source);
    teams[side] = { ...rest, hand: viewerSide === side ? hand : null, handCount: hand.length, deckCount: deck.length };
  }
  return { ...clone(state), teams };
}
